import { Skeleton, styled } from "@mui/material";
import { ButtonContainer } from "@common/Buttons";
import { InlinePlayerImage, VideoPlaceholder } from "@pageContent/ArticleInlinePlayer/InlinePlayer";
import { SocialSharingContainer } from "@pageContent/SocialSharing/SocialSharing";
import PageContainer from "@pageContent/common/PageContainer";
import * as StyledCustomVideoError from "@video/VideoPlayer/CustomVideoError.styled";
import * as StyledVideoPlayer from "@video/VideoPlayer/VideoPlayer.styled";
import { colors, fonts } from "@constants/cssVariables";

export const Container = styled("div")<{ isShortFormPage?: boolean }>(({ theme, isShortFormPage }) => ({
  backgroundColor: colors.neutral90,
  paddingBottom: theme.spacing(8),

  [theme.breakpoints.up("md")]: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(10),
  },
  ...(isShortFormPage && {
    marginBottom: theme.spacing(12),
    [`${Title}`]: {
      font: fonts.header4,
      marginBottom: theme.spacing(2),
    },
  }),
}));

export const Grid = styled(PageContainer)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, 1fr)",
  gap: theme.spacing(6),
}));

export const VideoWrapper = styled("div")(({ theme }) => ({
  gridColumn: "8 span",
  [theme.breakpoints.down("md")]: {
    gridColumn: "12 span",
    width: `calc(100% + ${theme.spacing(4 * 2)})`,
    marginLeft: theme.spacing(-4),
  },

  [`${VideoPlaceholder}, ${InlinePlayerImage}, .jwplayer, ${StyledVideoPlayer.PlayerContainer},${StyledVideoPlayer.VideoSkeleton}, ${StyledCustomVideoError.MessageContainer}`]:
    {
      borderRadius: 0,
      backgroundColor: colors.neutral80,
    },
}));

export const DetailsWrapper = styled("div")(({ theme }) => ({
  gridColumn: "4 span",
  position: "relative",
  color: colors.neutral0,
  [theme.breakpoints.down("md")]: {
    gridColumn: "12 span",
  },
  [`${ButtonContainer}`]: {
    backgroundColor: colors.neutral80,
    "&:hover": {
      backgroundColor: colors.neutral50,
    },
  },
  [`${SocialSharingContainer}`]: {
    marginTop: theme.spacing(6),
    marginBottom: 0,
  },
}));

export const Title = styled("h2")({
  font: fonts.header3,
});

export const Subtitle = styled("h3")({
  font: fonts.bodyMedium,
  color: colors.neutral30,
});

export const AiredDate = styled("p")(({ theme }) => ({
  font: fonts.bodySmall,
  color: colors.neutral40,
  marginBottom: theme.spacing(4),
}));

export const DescriptionWrapper = styled("div", { shouldForwardProp: (prop) => prop !== "maxHeight" })<{
  maxHeight: string | number;
}>(({ theme, maxHeight }) => ({
  [theme.breakpoints.up("md")]: {
    maxHeight,
    overflowY: "scroll",
    overflowX: "hidden",
    "&::-webkit-scrollbar": {
      width: 7,
    },

    "&::-webkit-scrollbar-thumb": {
      background: colors.neutral50,
      borderRadius: 8,
    },
  },
}));

export const Description = styled("p", { shouldForwardProp: (prop) => prop !== "isExpanded" })<{ isExpanded: boolean }>(
  ({ theme, isExpanded }) => ({
    font: fonts.bodyMedium,
    color: colors.neutral30,
    ...(!isExpanded && {
      overflow: "hidden",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      maxHeight: 48,
    }),
    [theme.breakpoints.up("md")]: {
      WebkitLineClamp: 4,
      paddingRight: theme.spacing(5),
      maxHeight: !isExpanded ? 104 : undefined,
    },
  })
);

export const ReadMoreButton = styled("button")({
  font: fonts.bodyMediumStrong,
  color: colors.neutral20,
  background: "none",
  border: "none",
  cursor: "pointer",
});

export const HeaderSkeleton: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <>
    <Container>
      <Grid>
        <VideoWrapper>
          <Skeleton
            data-testid="header-skeleton"
            variant="rectangular"
            width="100%"
            height="auto"
            component="div"
            sx={{ aspectRatio: "16 / 9", backgroundColor: colors.neutral80 }}
          />
        </VideoWrapper>
        <DetailsWrapper>
          <Skeleton width={220} variant="rounded" sx={{ marginBottom: 3, backgroundColor: colors.neutral80 }} />
          <Skeleton width={190} variant="rounded" sx={{ marginBottom: 2, backgroundColor: colors.neutral80 }} />
          <Skeleton width={120} variant="rounded" sx={{ marginBottom: 4, backgroundColor: colors.neutral80 }} />
          <Skeleton variant="rounded" sx={{ marginBottom: 2, backgroundColor: colors.neutral80 }} />
          <Skeleton width={"90%"} variant="rounded" sx={{ marginBottom: 2, backgroundColor: colors.neutral80 }} />
          <Skeleton width={84} variant="rounded" sx={{ marginBottom: 14, backgroundColor: colors.neutral80 }} />
        </DetailsWrapper>
      </Grid>
    </Container>
    {children}
  </>
);
